import { useEffect, useRef, useState } from 'react';

import Input from '../../common/Input';
import Modal from '../../common/Modal';
import {
  DialogCard,
  DialogFooter,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from './folderDialogStyles';

import type { FC, KeyboardEvent } from 'react';

type Props = {
  validate: (name: string) => string | null;
  onSubmit: (name: string) => Promise<void> | void;
  onClose: () => void;
};

const CreateFolderDialog: FC<Props> = ({ validate, onSubmit, onClose }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    const trimmed = value.trim();
    const validationError = validate(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(trimmed);
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <Modal isOpen closeModal={onClose}>
      <DialogCard data-testid="create-folder-dialog">
        <DialogTitle>New Folder</DialogTitle>
        <Input
          ref={inputRef}
          value={value}
          disabled={busy}
          fullWidth
          placeholder="Folder name"
          error={error ?? undefined}
          onChange={e => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKeyDown}
        />
        <DialogFooter>
          <SecondaryButton
            type="button"
            disabled={busy}
            onClick={onClose}
            data-testid="create-folder-cancel"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            data-testid="create-folder-confirm"
          >
            Create
          </PrimaryButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

export default CreateFolderDialog;
